import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Model } from "../../model/DrawingModel/DrawingModel";
import "./drawingresult.style.scss";
import {
  hideScore,
  setActiveModelDrawn,
  setStartImageProcessing,
  setTimerDone,
} from "../../../state/actions";
import { Box, Typography, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { calcCanvasAndModelDim } from "../../../lib/CalcCanvasAndModelDim";

const DRAWING_RESULT_CLASS = "drawing-result";
export const DRAWING_RESULT_DISPLAY_TIME = 20000;
export const DRAWING_RESULT_DISPLAY_TIME_COMPETE = 10000;

type Props = {
  isAuthenticated: boolean;
  canvasWidth: number;
  canvasHeight: number;
  innerModelWidth: number;
  innerModelHeight: number;
  scoreClass: string;
  baseURL: string;
  isInHomePage: boolean;
  activeModel: Model;
  currentScore: number;
  imgPath: string;
  model: Model;
  isCompeting?: boolean;
};
type MappedProps = {
  drawingEvaluationError: string;
  hideScore: () => void;
  setTimerDone: (bool: boolean) => void;
  setStartImageProcessing: (bool: boolean) => void;
  setActiveModelDrawn: () => void;
  lineBalance?: number;
};

const DrawingResult = ({
  canvasWidth,
  canvasHeight,
  innerModelWidth,
  innerModelHeight,
  scoreClass,
  activeModel,
  model,
  baseURL,
  drawingEvaluationError,
  currentScore,
  lineBalance,
  hideScore,
  setTimerDone,
  setStartImageProcessing,
  setActiveModelDrawn,
  isAuthenticated,
  isCompeting
}: Props & MappedProps) => {
  const [animationKey, setAnimationKey] = useState(0);
  const [resultProgress, setResultProgress] = useState(1);

  useEffect(() => {
    if (scoreClass === "drawing-result--show") {
      setAnimationKey((k) => k + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoreClass]);

  // Info overlays
  const [showAccuracyInfo, setShowAccuracyInfo] = useState(false);
  const [showBalanceInfo, setShowBalanceInfo] = useState(false);
  const [showXpInfo, setShowXpInfo] = useState(false);

  // Collapse info popovers whenever results are not being shown
  useEffect(() => {
    if (scoreClass !== "drawing-result--show") {
      setShowAccuracyInfo(false);
      setShowBalanceInfo(false);
      setShowXpInfo(false);
    }
  }, [scoreClass]);

  // Animate remaining time progress bar while results are visible
  useEffect(() => {
    if (scoreClass !== "drawing-result--show") {
      setResultProgress(0);
      return;
    }
    const total = isCompeting ? DRAWING_RESULT_DISPLAY_TIME_COMPETE : DRAWING_RESULT_DISPLAY_TIME;
    setResultProgress(1);
    let raf: number;
    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = now - start;
      const rem = Math.max(0, 1 - elapsed / total);
      setResultProgress(rem);
      if (rem > 0) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [scoreClass, isCompeting]);
  const handleSkip = () => {
    hideScore();
    setTimerDone(true);
    setStartImageProcessing(false);
    setActiveModelDrawn();
  };

  const clampedScore = Math.max(0, Math.min(typeof currentScore === "number" ? currentScore : 0, 100));
  // Responsive sizes: when layout is column (isWrapped) use full width; when row (wide screens), use legacy fixed sizes
  const { isWrapped } = calcCanvasAndModelDim();
  const effectiveWidth = Math.max(0, (canvasWidth || 0) * (isWrapped ? 1 : 0.5));
  const baseDim = Math.max(0, Math.min(effectiveWidth, canvasHeight || 0));

  const containerSize = isWrapped
    ? Math.max(110, Math.min(220, Math.round((baseDim || 800) * 0.25)))
    : 150;
  const gradientBarWidth = isWrapped
    ? Math.max(160, Math.min(260, Math.round((effectiveWidth || 800) * 0.30)))
    : 220;
  const balanceBarWidth = isWrapped
    ? Math.max(180, Math.min(300, Math.round((effectiveWidth || 800) * 0.34)))
    : 240;

  // Map 0→100 to red→green with lighter red at low and darker green at high
  const getAccuracyColor = (score: number) => {
    const p = score / 100;
    const hue = 120 * p; // 0 (red) to 120 (green)
    const lightness = 65 - 25 * p; // 65% (light red) to 40% (darker green)
    const saturation = 75; // keep vivid
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };
  const ringColor = `hsla(0, 0%, 100%, 0.25)`; // subtle neutral ring

  // Line balance metric from server [-1 (missing) ... 0 ... +1 (extra)]
  // Derive from state or from window debug value if available
  const debugLineBalance = typeof (window as any)?.lineBalance === 'number' ? (window as any).lineBalance : undefined;
  const srcLineBalance = typeof lineBalance === 'number' ? lineBalance : (debugLineBalance ?? 0);
  const lineBalanceValue = Math.max(-1, Math.min(1, srcLineBalance));
  // XP calculation (mirrors server): combine accuracy and line balance
  const normalized = clampedScore; // already 0..100
  const balanceFactor = 1 - Math.min(1, Math.abs(lineBalanceValue));
  const xpPercent = Math.max(0, Math.min(100, Math.round(normalized * balanceFactor)));
  const xp = Math.max(5, Math.round(xpPercent / 5) * 5);

  const renderPenaltyGraph = (score: number, size: number, key?: number) => {
    const s = Math.max(0, Math.min(score, 100));
    const accuracyColor = getAccuracyColor(s);
    const maxAim = Math.round(size * 0.75);
    const minAim = Math.max(8, Math.round(size * 0.1));
    const aimSize = Math.round(minAim + (maxAim - minAim) * (1 - s / 100));

    return (
      <div
        key={`penalty-${key ?? 0}`}
        className={DRAWING_RESULT_CLASS + "__score-penalty"}
        role="img"
        aria-label={`Line accuracies ${s}`}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          // CSS custom properties for animation
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...( { ["--aim-size"]: `${aimSize}px`, ["--accent-color"]: accuracyColor } as any )
        }}
      >
        <div
          className={DRAWING_RESULT_CLASS + "__score-penalty-ring"}
          style={{ borderColor: ringColor }}
        />
        <div
          className={
            DRAWING_RESULT_CLASS +
            "__score-penalty-ring " +
            DRAWING_RESULT_CLASS +
            "__score-penalty-ring--mid"
          }
          style={{ borderColor: ringColor }}
        />
        <div
          className={DRAWING_RESULT_CLASS + "__score-penalty-center"}
          style={{ background: accuracyColor }}
        />
        <div
          className={DRAWING_RESULT_CLASS + "__score-penalty-aim"}
          style={{ width: `${aimSize}px`, height: `${aimSize}px`, background: accuracyColor }}
        />
      </div>
    );
  };

  return (
    <div
      id="userScore"
      className={DRAWING_RESULT_CLASS + " " + scoreClass}
      style={{
        width: `${canvasWidth}px`,
        height: `${canvasHeight}px`,
        marginBottom: `-${canvasHeight}px`,
      }}
      onClick={() => {}}
    >
      {baseURL ? (
        <React.Fragment>
          <img
            alt="Drawing result"
            className={DRAWING_RESULT_CLASS + "__drawing"}
            draggable="false"
            style={{
              width: `${canvasWidth}px`,
              height: `${canvasHeight}px`,
              marginBottom: `-${canvasHeight}px`,
            }}
            src={baseURL}
          />

          <img
            alt="Original model on drawing result"
            className={DRAWING_RESULT_CLASS + "__model"}
            draggable="false"
            style={{
              width: `${innerModelWidth}px`,
              height: `${innerModelHeight}px`,
            }}
            src={
              activeModel.modelDataURL ? activeModel.modelDataURL : model.url
            }
          />

          <span className={DRAWING_RESULT_CLASS + "__score"}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 , flexDirection: "column" }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ fontSize: "16px" }}>Line accuracy</Typography>
                <IconButton size="small" aria-label="Line accuracy info" onClick={() => setShowAccuracyInfo((v) => !v)}>
                  <InfoOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Box>
              {showAccuracyInfo && (
                <div className={DRAWING_RESULT_CLASS + "__info-popover"}>
                  Comparing to the model, despite the slight difference in the position and scale on the canvas
                </div>
              )}

              {renderPenaltyGraph(clampedScore, containerSize, animationKey)}
              <div className={DRAWING_RESULT_CLASS + "__score-gradient"}>
                <div className={DRAWING_RESULT_CLASS + "__score-gradient-bar"} style={{ width: `${gradientBarWidth}px` }} />
                <div className={DRAWING_RESULT_CLASS + "__score-gradient-labels"} style={{ width: `${gradientBarWidth}px` }}>
                  <span>Low</span>
                  <span>High</span>
                </div>
              </div>

              {/* Line balance mini chart */}
              <div className={DRAWING_RESULT_CLASS + "__score__balance"} style={{ width: `${balanceBarWidth}px` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" sx={{ fontSize: "16px", color: "black" }}>Line quantity balance</Typography>
                  <IconButton size="small" aria-label="Line quantity balance info" onClick={() => setShowBalanceInfo((v) => !v)}>
                    <InfoOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Box>
                {showBalanceInfo && (
                  <div className={DRAWING_RESULT_CLASS + "__info-popover"}>
                    Comparing to the lines of the model
                  </div>
                )}

                <div className={DRAWING_RESULT_CLASS + "__score__balance-bar"} style={{ width: `${balanceBarWidth}px` }}>
                  <div
                    className={DRAWING_RESULT_CLASS + "__score__balance-marker"}
                    style={{ left: `${(lineBalanceValue + 1) * 50}%` }}
                  />
                </div>
                <div className={DRAWING_RESULT_CLASS + "__score__balance-labels"} style={{ width: `${balanceBarWidth}px` }}>
                  <span>Fewer lines</span>
                  <span>Balanced</span>
                  <span>More lines</span>
                </div>
              </div>
            </Box>
          </span>

          {/* XP panel */}
          <div className={DRAWING_RESULT_CLASS + "__xp"}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        
              <div className={DRAWING_RESULT_CLASS + "__xp-value"}>+{xp} XP</div>
              <IconButton size="small" aria-label="XP info" onClick={() => setShowXpInfo((v)=>!v)}>
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            </Box>
            {showXpInfo && (
              <div className={DRAWING_RESULT_CLASS + "__info-popover"} style={{ marginBottom: '8px' }}>
                XP combines your line accuracy with the amount of lines drawn versus the model. We add XP when it improves your best for this model at the same angle, or when you draw a new angle.
              </div>
            )}
            <div className={DRAWING_RESULT_CLASS + "__xp-text"}>
              {/** If authenticated show leaderboard link; else show signup link **/}
              { isAuthenticated && !isCompeting ? (
                <a className={DRAWING_RESULT_CLASS + "__xp-link"} href="/leaderboard">
                  View your rank on leaderboard
                  <ArrowForwardIosIcon sx={{ fontSize: 12, marginLeft: "6px" }} />
                </a>
              ) : !isCompeting && (
                <a className={DRAWING_RESULT_CLASS + "__xp-link"} href="/signup">
                  Login or signup to track your score
                  <ArrowForwardIosIcon sx={{ fontSize: 12, marginLeft: "6px" }} />
                </a>
              )}
            </div>
          </div>

          {!isCompeting && (
          <button
            aria-label="Close results"
            onClick={handleSkip}
            className={DRAWING_RESULT_CLASS + "__close"}
          >
            ×
          </button>
          )}
        </React.Fragment>
      ) : (
        <span
          style={{
            position: "absolute",
            width: "400px",
            textAlign: "center",
            top: `${canvasHeight / 2 - 50}px`,
            left: `${canvasWidth / 2 - 200}px`,
            color: "white",
            fontSize: "28px",
          }}
        >
          {drawingEvaluationError}
        </span>
      )}

      {/* Remaining time progress bar */}
      {scoreClass === "drawing-result--show" && (
        <div
          className={DRAWING_RESULT_CLASS + "__timer"}
          style={{ width: `${balanceBarWidth}px` }}
          aria-label="Result auto-close timer"
        >
          <div
            className={DRAWING_RESULT_CLASS + "__timer-bar"}
            style={{ width: `${Math.max(0, Math.min(100, Math.round(resultProgress * 100)))}%` }}
          />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  const {
    currentScore,
    scoreClass,
    leftHand,
    activeModel,
    canvasWidth,
    canvasHeight,
    innerModelWidth,
    innerModelHeight,
    drawingEvaluationError,
    lineBalance,
    isAuthenticated,
  } = state;
  return {
    currentScore,
    scoreClass,
    leftHand,
    activeModel,
    canvasWidth,
    canvasHeight,
    innerModelWidth,
    innerModelHeight,
    drawingEvaluationError,
    lineBalance,
    isAuthenticated,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    hideScore: () => dispatch(hideScore()),
    setTimerDone: (bool) => dispatch(setTimerDone(bool)),
    setStartImageProcessing: (bool) => dispatch(setStartImageProcessing(bool)),
    setActiveModelDrawn: () => dispatch(setActiveModelDrawn()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DrawingResult);
